<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- CRT scanlines overlay -->
      <div class="crt" aria-hidden="true"></div>

      <!-- Grid floor perspective -->
      <div class="grid-floor" aria-hidden="true"></div>

      <!-- Header -->
      <header class="neon-header">
        <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="nh-logo" />
        <div class="nh-title-block">
          <div class="nh-marquee">
            <span v-for="n in 6" :key="n">✦ MORTGAGE SOLUTIONS ✦ HOME LOANS ✦ PARTNERSHIP &nbsp;</span>
          </div>
        </div>
  
      </header>

      <!-- Hero -->
      <section class="neon-hero">
        <div class="neon-sun" aria-hidden="true">
          <div class="sun-segment" v-for="n in 8" :key="n"></div>
        </div>
        <div class="hero-content">
          <div class="hc-glitch-label">LOAN_SYSTEM_v2.0</div>
          <h1 class="neon-h1" v-html="heroH1Html"></h1>
          <p class="neon-p">{{ heroBody }}</p>
          <div class="neon-btns">
            <a class="nb-primary" :href="applyRightLink">▶ {{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="nb-sec" :href="applyLeftLink">◈ {{ heroSecondaryText }}</a>
          </div>
          <div class="neon-contacts">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="nc-item">
              ☎ {{ loinfo.phone_number }}
            </a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="nc-item">
              ✉ {{ loinfo.email }}
            </a>
          </div>
        </div>
      </section>

      <!-- Agent cards — arcade score-board style -->
      <section class="neon-board">
        <div class="nb-header">— AGENT DIRECTORY —</div>

        <div class="arcade-card lo-arc">
          <div class="ac-glow lo-glow"></div>
          <div class="ac-inner">
            <div class="ac-rank lo-rank">P1</div>
            <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="ac-img" />
            <div class="ac-info">
              <div class="ac-tag lo-tag">LOAN OFFICER</div>
              <div class="ac-name">{{ loName }}</div>
              <div class="ac-role">{{ loTitle }}</div>
              <div v-if="loinfo && loinfo.nmls" class="ac-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              <div class="ac-contacts">
                <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="acc lo-acc">{{ loinfo.phone_number }}</a>
                <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="acc lo-acc">{{ loinfo.email }}</a>
              </div>
            </div>
          </div>
       
        </div>

        <div class="arcade-card re-arc">
          <div class="ac-glow re-glow"></div>
          <div class="ac-inner">
            <div class="ac-rank re-rank">P2</div>
            <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="ac-img" />
            <div class="ac-info">
              <div class="ac-tag re-tag">PARTNER</div>
              <div class="ac-name">{{ realtorName }}</div>
              <div class="ac-role">{{ realtor && realtor.brokerage_name }}</div>
              <div class="ac-contacts">
                <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="acc re-acc">{{ realtor.phone_number }}</a>
                <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="acc re-acc">{{ realtor.email }}</a>
                <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="acc re-acc" target="_blank" rel="noreferrer">WEBSITE →</a>
              </div>
            </div>
          </div>
        </div>

      </section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandNeon",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Level up your<br/><em>home game.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Audiowide&family=Exo+2:wght@300;400;600&display=swap');

.page {
  min-height: 100vh;
  background: #08001a;
  color: #e0d0ff;
  font-family: 'Exo 2', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --pink: #ff2d8a;
  --cyan: #00f5ff;
  --purple: #8b2fff;
  --yellow: #ffe600;
  --bg: #08001a;
  --border: rgba(0,245,255,0.18);
  --muted: #8878aa;
  overflow: hidden;
  position: relative;
}
.root { position: relative; z-index: 1; display: flex; flex-direction: column; min-height: 100vh; }

/* CRT */
.crt {
  position: fixed; inset: 0; pointer-events: none; z-index: 200;
  background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px);
}

/* GRID FLOOR */
.grid-floor {
  position: fixed; bottom: 0; left: 0; right: 0; height: 40vh; pointer-events: none; z-index: 0;
  background:
    linear-gradient(rgba(0,245,255,0.12) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,245,255,0.12) 1px, transparent 1px);
  background-size: 60px 60px;
  transform: perspective(400px) rotateX(55deg);
  transform-origin: bottom center;
  mask-image: linear-gradient(to top, rgba(0,0,0,0.5), transparent);
}

/* SUN */
.neon-sun { position: absolute; bottom: -80px; left: 50%; transform: translateX(-50%); width: 280px; height: 140px; border-radius: 140px 140px 0 0; overflow: hidden; z-index: 0; }
.sun-segment { height: 18px; margin-bottom: 6px; }
.sun-segment:nth-child(1) { background: var(--pink); }
.sun-segment:nth-child(2) { background: color-mix(in srgb, var(--pink) 80%, var(--yellow)); }
.sun-segment:nth-child(3) { background: color-mix(in srgb, var(--pink) 60%, var(--yellow)); }
.sun-segment:nth-child(4) { background: color-mix(in srgb, var(--pink) 40%, var(--yellow)); }
.sun-segment:nth-child(5) { background: var(--yellow); }
.sun-segment:nth-child(6) { background: color-mix(in srgb, var(--yellow) 70%, var(--cyan)); }
.sun-segment:nth-child(7) { background: var(--cyan); }
.sun-segment:nth-child(8) { background: color-mix(in srgb, var(--cyan) 70%, var(--purple)); }

/* HEADER */
.neon-header { position: relative; z-index: 10; display: flex; align-items: center; justify-content: space-between; padding: 16px 48px; border-bottom: 1px solid var(--border); background: rgba(8,0,26,0.9); gap: 24px; overflow: hidden; }
.nh-logo { width: 130px; max-height: 68px; object-fit: contain; filter: brightness(0) invert(0.7) sepia(1) hue-rotate(240deg) saturate(3); flex-shrink: 0; }
.nh-title-block { flex: 1; overflow: hidden; }
.nh-marquee { display: inline-block; white-space: nowrap; animation: marquee 20s linear infinite; font-family: 'Press Start 2P', monospace; font-size: 7px; color: var(--cyan); opacity: 0.6; letter-spacing: 0.1em; }
@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.nh-cta { font-family: 'Press Start 2P', monospace; font-size: 8px; text-decoration: none; background: transparent; color: var(--pink); border: 2px solid var(--pink); padding: 10px 18px; border-radius: 2px; white-space: nowrap; letter-spacing: 0.06em; box-shadow: 0 0 16px rgba(255,45,138,0.4), inset 0 0 16px rgba(255,45,138,0.05); transition: box-shadow 0.2s; flex-shrink: 0; }
.nh-cta:hover { box-shadow: 0 0 30px rgba(255,45,138,0.7), inset 0 0 30px rgba(255,45,138,0.1); }

/* HERO */
.neon-hero { position: relative; z-index: 1; padding: 60px 48px 40px; min-height: 380px; display: flex; align-items: center;margin:auto }
.hero-content { position: relative; z-index: 2; max-width: 620px; }
.hc-glitch-label { font-family: 'Press Start 2P', monospace; font-size: 8px; color: var(--cyan); letter-spacing: 0.15em; margin-bottom: 18px; opacity: 0.7; }
.neon-h1 { font-family: 'Audiowide', sans-serif; font-size: clamp(36px, 5.5vw, 68px); line-height: 1.08; color: #fff; margin: 0 0 20px; text-shadow: 0 0 20px rgba(139,47,255,0.6), 0 0 60px rgba(139,47,255,0.2); }
.neon-h1 em { font-style: normal; color: var(--pink); text-shadow: 0 0 20px rgba(255,45,138,0.8), 0 0 60px rgba(255,45,138,0.3); }
.neon-p { font-size: 14px; line-height: 1.76; color: var(--muted); margin-bottom: 28px; font-weight: 300; }
.neon-btns { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; justify-content: center; }
.nb-primary { font-family: 'Press Start 2P', monospace; font-size: 8px; text-decoration: none; background: var(--cyan); color: #08001a; padding: 14px 24px; border-radius: 2px; letter-spacing: 0.08em; width: 55%; text-align: center; box-shadow: 0 0 24px rgba(0,245,255,0.5); transition: box-shadow 0.2s; }
.nb-primary:hover { box-shadow: 0 0 40px rgba(0,245,255,0.8); }
.nb-sec { font-family: 'Press Start 2P', monospace; font-size: 8px; text-decoration: none; color: var(--purple); border: 2px solid var(--purple); padding: 14px 16px; border-radius: 2px; letter-spacing: 0.04em; box-shadow: 0 0 12px rgba(139,47,255,0.3); transition: box-shadow 0.2s; }
.nb-sec:hover { box-shadow: 0 0 24px rgba(139,47,255,0.6); }
.neon-contacts { display: flex; flex-direction: column; gap: 6px; }
.nc-item { font-family: 'Press Start 2P', monospace; font-size: 7px; text-decoration: none; color: var(--muted); letter-spacing: 0.1em; transition: color 0.18s; }
.nc-item:hover { color: var(--cyan); }

/* BOARD */
.neon-board { position: relative; z-index: 1; background: rgba(0,0,0,0.4); border-top: 1px solid var(--border); padding: 36px 48px 48px; display: flex; flex-direction: column; gap: 16px; }
.nb-header { font-family: 'Press Start 2P', monospace; font-size: 9px; color: var(--yellow); letter-spacing: 0.2em; text-align: center; margin-bottom: 8px; text-shadow: 0 0 12px rgba(255,230,0,0.6); }

.arcade-card { border: 2px solid; border-radius: 4px; overflow: hidden; position: relative; max-width: 680px; margin: 0 auto; width: 100%; }
.lo-arc { border-color: var(--cyan); box-shadow: 0 0 20px rgba(0,245,255,0.2), inset 0 0 20px rgba(0,245,255,0.03); }
.re-arc { border-color: var(--pink); box-shadow: 0 0 20px rgba(255,45,138,0.2), inset 0 0 20px rgba(255,45,138,0.03); }
.ac-glow { position: absolute; top: 0; left: 0; right: 0; height: 2px; }
.lo-glow { background: linear-gradient(90deg, transparent, var(--cyan), transparent); }
.re-glow { background: linear-gradient(90deg, transparent, var(--pink), transparent); }
.ac-inner { display: flex; gap: 16px; align-items: flex-start; padding: 18px; background: rgba(8,0,26,0.8); }
.ac-rank { font-family: 'Press Start 2P', monospace; font-size: 14px; flex-shrink: 0; padding: 8px; }
.lo-rank { color: var(--cyan); text-shadow: 0 0 12px rgba(0,245,255,0.8); }
.re-rank { color: var(--pink); text-shadow: 0 0 12px rgba(255,45,138,0.8); }
.ac-img { width: 56px; height: 56px; border-radius: 4px; object-fit: cover; flex-shrink: 0; border: 1px solid var(--border); }
.ac-tag { font-family: 'Press Start 2P', monospace; font-size: 7px; letter-spacing: 0.16em; margin-bottom: 6px; }
.lo-tag { color: var(--cyan); }
.re-tag { color: var(--pink); }
.ac-name { font-family: 'Audiowide', sans-serif; font-size: 16px; color: #fff; margin-bottom: 2px; }
.ac-role { font-size: 11px; color: var(--muted); }
.ac-nmls { font-family: 'Press Start 2P', monospace; font-size: 7px; color: rgba(136,120,170,0.5); margin-top: 4px; }
.ac-contacts { display: flex; flex-direction: column; gap: 4px; margin-top: 8px; }
.acc { font-family: 'Press Start 2P', monospace; font-size: 7px; text-decoration: none; letter-spacing: 0.08em; transition: color 0.18s; }
.lo-acc { color: rgba(0,245,255,0.6); }
.lo-acc:hover { color: var(--cyan); }
.re-acc { color: rgba(255,45,138,0.6); }
.re-acc:hover { color: var(--pink); }
.ac-cta { display: block; text-align: center; text-decoration: none; font-family: 'Press Start 2P', monospace; font-size: 8px; letter-spacing: 0.1em; padding: 12px; background: rgba(0,245,255,0.1); border-top: 1px solid var(--cyan); color: var(--cyan); transition: background 0.2s; }
.ac-cta:hover { background: rgba(0,245,255,0.2); }

@media (max-width: 820px) {
  .neon-header { padding: 12px 24px; }
  .nh-title-block { display: none; }
  .neon-hero { padding: 40px 24px; }
  .neon-board { padding: 24px; }
}
</style>
